'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { cn } from '@/utils/cn';

const DEFAULT_IMAGE = '/images/abt_ns_img.png';

const DEFAULT_HEADING = 'Mission and Vision';

const DEFAULT_DESCRIPTION =
  'We aim to be the most trusted brand in building solutions, rooted in strong values and committed to the society we help build. We are dedicated to delivering that trust through superior products, sustainable practices, advanced technology, and people who uphold our standards at every level.';

export type AboutMissionVisionScrollProps = {
  heading?: string;
  description?: string;
  imageSrc?: string;
  imageAlt?: string;
  /**
   * Pin + scrub begin when section top is this far above the viewport top (px).
   * Use `0` for `top top`. GSAP: `top+=pinStartOffsetPx top`.
   */
  pinStartOffsetPx?: number;
  /** Scroll distance (vh) while pinned — animation completes at progress 1. */
  pinScrollVh?: number;
  className?: string;
};

/** Vertical inset of the card inside the container column (px). */
const FRAME_FROM = {
  top: 48,
  bottom: 48,
  borderRadius: 22,
} as const;

/** Tiny fudge so scaled edges don’t show a hairline gap. */
const SCALE_BLEED = 1.02;

export default function AboutMissionVisionScroll({
  heading = DEFAULT_HEADING,
  description = DEFAULT_DESCRIPTION,
  imageSrc = DEFAULT_IMAGE,
  imageAlt = 'Wonder Cement team reviewing plans on site',
  pinStartOffsetPx = 0,
  pinScrollVh = 110,
  className,
}: AboutMissionVisionScrollProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const section = sectionRef.current;
    const shell = shellRef.current;
    const container = containerRef.current;
    const frame = frameRef.current;
    if (!section || !shell || !container || !frame) return;

    gsap.registerPlugin(ScrollTrigger);

    const media = gsap.matchMedia();
    let rafInner = 0;

    media.add('(min-width: 992px)', () => {
      let ctx: gsap.Context | null = null;

      const build = () => {
        ctx?.revert();

        const rect = frame.getBoundingClientRect();
        const rw = Math.max(1, rect.width);
        const rh = Math.max(1, rect.height);
        const endScale = Math.max(window.innerWidth / rw, window.innerHeight / rh) * SCALE_BLEED;

        ctx = gsap.context(() => {
          gsap.set(frame, { transformOrigin: '50% 50%' });
          gsap.fromTo(
            frame,
            {
              scale: 1,
              borderRadius: FRAME_FROM.borderRadius,
            },
            {
              scale: endScale,
              borderRadius: 0,
              ease: 'none',
              force3D: true,
              scrollTrigger: {
                trigger: section,
                start: `top+=${pinStartOffsetPx} top`,
                end: '+=100%',
                pin: true,
                pinSpacing: true,
                scrub: 0.6,
                anticipatePin: 1,
                invalidateOnRefresh: true,
              },
            },
          );
        }, section);
      };

      const rafOuter = window.requestAnimationFrame(() => {
        rafInner = window.requestAnimationFrame(build);
      });

      window.addEventListener('resize', build);

      return () => {
        window.cancelAnimationFrame(rafOuter);
        window.cancelAnimationFrame(rafInner);
        window.removeEventListener('resize', build);
        ctx?.revert();
      };
    });

    return () => {
      media.revert();
    };
  }, [pinScrollVh, pinStartOffsetPx]);

  return (
    <section
      ref={sectionRef}
      className={cn('relative w-full overflow-hidden bg-[#f0f0f0]', className)}
    >
      {/* <div className="h-28 bg-gradient-to-b from-transparent to-black from-0% to-50%  absolute bottom-0 left-0 right-0 z-[99]" /> */}
      <div className="min-[992px]:hidden">
        <div className="relative aspect-[16/11] w-full overflow-hidden">
          <CmsImage
            src={imageSrc}
            alt={imageAlt}
            fill
            className="object-cover object-top"
            sizes="100vw"
            priority={false}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.5)_0%,rgba(0,0,0,0.2)_45%,rgba(0,0,0,0.15)_100%)]"
            aria-hidden
          />
        </div>

        <div className="container mx-auto px-4 py-10 text-center sm:px-6 sm:py-12">
          <h2 className="mx-auto max-w-4xl text-3xl font-semibold tracking-tight text-black sm:text-4xl">
            {heading}
          </h2>
          <div
            className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-black/95 sm:text-base"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </div>

      <div className="hidden min-[992px]:block">
        <div ref={shellRef} className="relative h-[100svh] min-h-[520px] w-full overflow-visible">
          <div ref={containerRef} className="container relative mx-auto h-full px-4 md:px-6">
            <div
              ref={frameRef}
              className="absolute left-0 right-0 z-10 overflow-hidden shadow-2xl h-full"
              style={{
                top: FRAME_FROM.top,
                bottom: FRAME_FROM.bottom,
                borderRadius: FRAME_FROM.borderRadius,
                transform: 'scale(1)',
                transformOrigin: 'center center',
                willChange: 'transform, border-radius',
              }}
            >
              <CmsImage
                src={imageSrc}
                alt={imageAlt}
                fill
                className="object-cover object-top"
                sizes="1280px"
                priority={false}
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
              <div
                className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0.2)_38%,rgba(0,0,0,0.1)_100%)]"
                aria-hidden
              />
            </div>

            <div className="pointer-events-none relative z-20 flex h-full flex-col items-center px-4 py-24 text-center sm:px-6">
              <h2 className="max-w-4xl text-4xl font-semibold tracking-tight text-white lg:text-[2.5rem]">
                {heading}
              </h2>
              <div
                className="mt-8 max-w-3xl text-base leading-relaxed text-white/95 lg:text-lg"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
